import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useToast } from '../components/ToastProvider';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import type { PaginatedOvpnKeys, PaginatedVpnKeys } from '../services/types';
import { notifyApiResult } from '../utils/notify';
import { formatDate } from '../utils/format';

type Protocol = 'wg' | 'ovpn';

export function VpnPage() {
  const { user } = useAuth();
  const toast = useToast();
  const canWrite = user?.role === 'admin' || user?.role === 'superadmin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [wgData, setWgData] = useState<PaginatedVpnKeys | null>(null);
  const [ovpnData, setOvpnData] = useState<PaginatedOvpnKeys | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importBusy, setImportBusy] = useState(false);

  const protocol = (searchParams.get('protocol') || 'wg') as Protocol;
  const page = Number(searchParams.get('page') || '1');
  const q = searchParams.get('q') || '';
  const server_code = searchParams.get('server_code') || '';
  const status = searchParams.get('status') || 'active';
  const user_id = searchParams.get('user_id') || '';

  const [qInput, setQInput] = useState(q);
  const [userIdInput, setUserIdInput] = useState(user_id);
  const [serverInput, setServerInput] = useState(server_code);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      page,
      page_size: 20,
      q: q || undefined,
      server_code: server_code || undefined,
      status: status !== 'all' ? status : 'all',
      user_id: user_id && /^\d+$/.test(user_id) ? Number(user_id) : undefined,
    };
    const res =
      protocol === 'ovpn' ? await api.listOvpnKeys(params) : await api.listVpnKeys(params);
    if (res.data) {
      if (protocol === 'ovpn') setOvpnData(res.data as PaginatedOvpnKeys);
      else setWgData(res.data as PaginatedVpnKeys);
    } else if (res.error) setError(res.error.message);
    setLoading(false);
  }, [page, q, server_code, status, user_id, protocol]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (qInput) next.set('q', qInput);
    else next.delete('q');
    if (userIdInput) next.set('user_id', userIdInput);
    else next.delete('user_id');
    if (serverInput) next.set('server_code', serverInput);
    else next.delete('server_code');
    next.set('page', '1');
    setSearchParams(next);
  };

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSearchParams(next);
  };

  const setProtocol = (p: Protocol) => {
    const next = new URLSearchParams(searchParams);
    next.set('protocol', p);
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  const data = protocol === 'ovpn' ? ovpnData : wgData;

  const importOrphans = async () => {
    const code = (serverInput || server_code || '').trim().toLowerCase();
    if (!code) {
      toast.error('Укажите server_code (например lv) в фильтре');
      return;
    }
    setImportBusy(true);
    try {
      const preview = await api.importWgOrphans({ server_code: code, dry_run: true });
      if (
        !notifyApiResult(
          toast,
          preview,
          (d) =>
            `Preview ${d.server_code.toUpperCase()}: к импорту ${d.to_import}, уже в БД ${d.already_ok}, bad name ${d.skipped_bad_name}`,
        )
      ) {
        return;
      }
      const d = preview.data!;
      if (d.to_import <= 0) {
        toast.success('Нечего импортировать — все клиенты уже в БД или имена не client_<id>');
        return;
      }
      const sample = d.samples
        .slice(0, 5)
        .map((s) => `user ${s.user_id} · ${s.name}`)
        .join('\n');
      const ok = window.confirm(
        `Импортировать ${d.to_import} orphan-ключей из WG-easy (${code.toUpperCase()}) в wg_keys?\n` +
          `Уже в БД: ${d.already_ok}, пропуск (имя): ${d.skipped_bad_name}\n\n` +
          (sample ? `Примеры:\n${sample}\n\n` : '') +
          'Только INSERT, без revoke.',
      );
      if (!ok) return;
      const apply = await api.importWgOrphans({ server_code: code, dry_run: false });
      if (
        notifyApiResult(
          toast,
          apply,
          (r) =>
            `Импорт ${r.server_code.toUpperCase()}: +${r.imported}/${r.to_import}` +
            (r.errors.length ? `, ошибок ${r.errors.length}` : ''),
        )
      ) {
        void load();
      }
    } catch {
      toast.error('Импорт orphan не удался');
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>VPN ключи</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canWrite && protocol === 'wg' ? (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={importBusy}
              onClick={() => void importOrphans()}
              title="Связать клиенты WG-easy без строк в wg_keys"
            >
              {importBusy ? 'Импорт…' : 'Импорт orphan из WG-easy'}
            </button>
          ) : null}
          <Link to="/servers" className="btn btn-ghost">
            Серверы →
          </Link>
        </div>
      </div>

      <div className="filters-row">
        <button
          type="button"
          className={`btn ${protocol === 'wg' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setProtocol('wg')}
        >
          WireGuard
        </button>
        <button
          type="button"
          className={`btn ${protocol === 'ovpn' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setProtocol('ovpn')}
        >
          OpenVPN
        </button>
      </div>

      <form onSubmit={applyFilters} className="filters-row">
        <input
          className="input"
          placeholder="Поиск: ID, client/profile, сервер…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <input
          className="input"
          placeholder="User ID"
          value={userIdInput}
          onChange={(e) => setUserIdInput(e.target.value)}
        />
        <input
          className="input"
          placeholder="Сервер (ru, lv…)"
          value={serverInput}
          onChange={(e) => setServerInput(e.target.value)}
          style={{ width: 120 }}
        />
        <select className="select" value={status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="active">Активные</option>
          <option value="deleted">Удалённые</option>
          <option value="all">Все</option>
        </select>
        <button type="submit" className="btn btn-primary">
          Найти
        </button>
      </form>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}

      {!loading && data && protocol === 'wg' && wgData && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Пользователь</th>
                  <th>Сервер</th>
                  <th>Client ID</th>
                  <th>Статус</th>
                  <th>Создан</th>
                </tr>
              </thead>
              <tbody>
                {wgData.items.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ color: 'var(--text-muted)' }}>
                      Ключи не найдены
                    </td>
                  </tr>
                )}
                {wgData.items.map((key) => (
                  <tr key={key.id}>
                    <td>
                      <Link to={`/vpn/${key.id}`}>{key.id}</Link>
                    </td>
                    <td>
                      <Link to={`/users/${key.user_id}`}>
                        {key.first_name || key.username || key.user_id}
                      </Link>
                    </td>
                    <td>
                      <code>{key.server_country}</code>
                    </td>
                    <td>
                      <code style={{ fontSize: '0.75rem' }}>{key.client_id.slice(0, 12)}…</code>
                    </td>
                    <td>
                      <span className={key.status === 'active' ? 'badge badge-success' : 'badge'}>
                        {key.status}
                      </span>
                    </td>
                    <td>{formatDate(key.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {wgData.pages > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
              <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                ←
              </button>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                {page} / {wgData.pages} ({wgData.total} всего)
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= wgData.pages}
                onClick={() => setPage(page + 1)}
              >
                →
              </button>
            </div>
          )}
        </>
      )}

      {!loading && data && protocol === 'ovpn' && ovpnData && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Пользователь</th>
                  <th>Сервер</th>
                  <th>Profile</th>
                  <th>Статус</th>
                  <th>Истекает</th>
                  <th>Создан</th>
                </tr>
              </thead>
              <tbody>
                {ovpnData.items.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ color: 'var(--text-muted)' }}>
                      OpenVPN ключи не найдены
                    </td>
                  </tr>
                )}
                {ovpnData.items.map((key) => (
                  <tr key={key.id}>
                    <td>{key.id}</td>
                    <td>
                      <Link to={`/users/${key.user_id}`}>
                        {key.first_name || key.username || key.user_id}
                      </Link>
                    </td>
                    <td>
                      <code>{key.server_code}</code>
                    </td>
                    <td>
                      <code style={{ fontSize: '0.75rem' }}>{key.profile_id}</code>
                    </td>
                    <td>
                      <span className={key.status === 'active' ? 'badge badge-success' : 'badge'}>
                        {key.status}
                      </span>
                    </td>
                    <td>{formatDate(key.expires_at)}</td>
                    <td>{formatDate(key.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ovpnData.pages > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
              <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                ←
              </button>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                {page} / {ovpnData.pages} ({ovpnData.total} всего)
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= ovpnData.pages}
                onClick={() => setPage(page + 1)}
              >
                →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
